'use client';

import { useEffect, useRef } from 'react';
import {
  PREVIEW_CAPTURE_EVENT,
  type PreviewCaptureRequest,
  type PreviewCaptureResult,
} from '@/lib/preview/capture';
import type { PreviewCaptureEventDetail } from '@/lib/preview/capture-client';
import { usePreviewAnnotationAwareness } from './PreviewAnnotationContext';

export function useAgentPreviewCapture(options: {
  enabled: boolean;
  matches: (request: PreviewCaptureRequest) => boolean;
  capture: (request: PreviewCaptureRequest, signal: AbortSignal) => Promise<PreviewCaptureResult>;
}) {
  const { scopeKey } = usePreviewAnnotationAwareness();
  const current = useRef(options);
  current.current = options;
  useEffect(() => {
    const pending = new Set<AbortController>();
    const receive = (raw: Event) => {
      const event = raw as CustomEvent<PreviewCaptureEventDetail>;
      const { request, signal, respond } = event.detail;
      if (
        event.defaultPrevented ||
        !current.current.enabled ||
        scopeKey !== `${request.projectId}:${request.sessionId}` ||
        !current.current.matches(request)
      )
        return;
      event.preventDefault();
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) controller.abort();
      pending.add(controller);
      void current.current
        .capture(request, controller.signal)
        .then(result => {
          if (
            !controller.signal.aborted &&
            current.current.enabled &&
            current.current.matches(request)
          )
            respond(result);
          else respond({ status: 'stale', error: 'The editor changed during capture.' });
        })
        .catch(error =>
          respond({
            status: 'unavailable',
            error: error instanceof Error ? error.message.slice(0, 500) : 'Preview capture failed.',
          })
        )
        .finally(() => {
          pending.delete(controller);
          signal.removeEventListener('abort', abort);
        });
    };
    window.addEventListener(PREVIEW_CAPTURE_EVENT, receive);
    return () => {
      window.removeEventListener(PREVIEW_CAPTURE_EVENT, receive);
      for (const controller of pending) controller.abort();
    };
  }, [scopeKey]);
}
